import { Filter, X } from 'lucide-react'

interface OKRFiltersProps {
  filters: {
    priority: string | null
    status: string | null
    owner: string | null
  }
  owners: Array<{ id: string; name: string }>
  onFilterChange: (filters: any) => void
  onClearFilters: () => void
}

const priorityOptions = [
  { value: 'CRITICAL', label: 'Critical', color: 'red' },
  { value: 'HIGH', label: 'High', color: 'orange' },
  { value: 'MEDIUM-HIGH', label: 'Medium-High', color: 'yellow' },
  { value: 'MEDIUM', label: 'Medium', color: 'blue' },
]

const statusOptions = [
  { value: 'on_track', label: 'On Track' },
  { value: 'at_risk', label: 'At Risk' },
  { value: 'off_track', label: 'Off Track' },
  { value: 'completed', label: 'Completed' },
]

export default function OKRFilters({
  filters,
  owners,
  onFilterChange,
  onClearFilters,
}: OKRFiltersProps) {
  const hasActiveFilters = filters.priority || filters.status || filters.owner
  const activeFilterCount = [filters.priority, filters.status, filters.owner].filter(Boolean).length

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Filter className="w-5 h-5 text-graphite-grey" />
          <h3 className="font-semibold text-vampire-black">Filters</h3>
          {activeFilterCount > 0 && (
            <span className="px-2 py-1 text-xs font-semibold bg-royal-blue text-white rounded-full">
              {activeFilterCount}
            </span>
          )}
        </div>
        {hasActiveFilters && (
          <button
            onClick={onClearFilters}
            className="text-sm text-royal-blue hover:text-royal-blue-700 flex items-center gap-1 transition-colors"
          >
            <X className="w-4 h-4" />
            Clear all
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Priority Filter */}
        <div>
          <label className="block text-sm font-medium text-graphite-grey mb-2">
            Priority
          </label>
          <select
            value={filters.priority || ''}
            onChange={(e) => onFilterChange({ ...filters, priority: e.target.value || null })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-royal-blue focus:border-transparent text-sm"
          >
            <option value="">All Priorities</option>
            {priorityOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        {/* Status Filter */}
        <div>
          <label className="block text-sm font-medium text-graphite-grey mb-2">
            Status
          </label>
          <select
            value={filters.status || ''}
            onChange={(e) => onFilterChange({ ...filters, status: e.target.value || null })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-royal-blue focus:border-transparent text-sm"
          >
            <option value="">All Statuses</option>
            {statusOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        {/* Owner Filter */}
        <div>
          <label className="block text-sm font-medium text-graphite-grey mb-2">
            Owner
          </label>
          <select
            value={filters.owner || ''}
            onChange={(e) => onFilterChange({ ...filters, owner: e.target.value || null })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-royal-blue focus:border-transparent text-sm"
          >
            <option value="">All Owners</option>
            {owners.map((owner) => (
              <option key={owner.id} value={owner.id}>
                {owner.name}
              </option>
            ))}
          </select>
        </div>
      </div>
    </div>
  )
}
